// Protocolos de tests de evaluación para 8-9 años.
// Orden recomendado de testeo: antropométricas → físicas (de alta a baja intensidad) → técnicas → mental.
// Basado en FIFA Talent ID, Reilly et al. (2000, J Sports Sci), LTAD.

import { Zap, Activity, Ruler, Brain } from 'lucide-react';

export const TEST_PROTOCOLS = [
  // ── Antropométricas primero (en reposo) ─────────────────
  {
    type: 'HEIGHT',
    label: 'Altura',
    icon: Ruler,
    unit: 'cm',
    group: 'Antropométrico',
    color: 'blue',
    equipment: ['Cinta métrica pegada a la pared o estadímetro'],
    instructions: [
      'Sin zapatos, talones juntos contra la pared.',
      'Espalda recta, mirada al frente.',
      'Registrá el punto más alto de la cabeza.',
    ],
    tip: 'Medí una vez por mes — cambia rápido a esta edad.',
    frequency: 'Mensual',
    inputType: 'number',
    step: 0.5,
  },
  {
    type: 'WEIGHT',
    label: 'Peso',
    icon: Ruler,
    unit: 'kg',
    group: 'Antropométrico',
    color: 'blue',
    equipment: ['Balanza digital'],
    instructions: [
      'Sin zapatos, ropa liviana.',
      'Preferente misma hora del día que la medición anterior.',
    ],
    tip: 'Con altura + peso calculás IMC. Rango sano 8-9 años: 14-18.',
    frequency: 'Mensual',
    inputType: 'number',
    step: 0.1,
  },

  // ── Físicas (alta intensidad — antes de fatigarse) ────
  {
    type: 'VERTICAL_JUMP',
    label: 'Salto vertical (Sargent)',
    icon: Zap,
    unit: 'cm',
    group: 'Físico',
    color: 'amber',
    equipment: ['Pared lisa', 'Tiza o cinta adhesiva', 'Regla o cinta métrica'],
    instructions: [
      'Jugador parado contra la pared con brazo estirado → marcá altura inicial.',
      'Pintá la punta de los dedos con tiza.',
      'Da 3 saltos verticales (contramovimiento, sin paso previo).',
      'Tocá la pared en el punto más alto posible.',
      'Diferencia entre la marca del salto y la marca inicial = salto vertical.',
      'Registrá el mejor de los 3 saltos.',
    ],
    reference: '20-28 cm bueno · >30 cm excelente',
    frequency: 'Cada 6-8 semanas',
    inputType: 'number',
    step: 0.5,
    timer: { label: 'Descanso entre saltos', seconds: 30 },
  },
  {
    type: 'SPRINT_20M',
    label: 'Sprint 20m',
    icon: Zap,
    unit: 's',
    group: 'Físico',
    color: 'amber',
    equipment: ['Cronómetro', '2 conos', 'Cinta métrica (20m)'],
    instructions: [
      'Marcá línea de salida y línea a 20m.',
      'Jugador parte de pie desde la línea de salida.',
      'A la señal "¡ya!" largás el cronómetro.',
      'Parás cuando el pecho cruza la línea de 20m.',
      '3 intentos con 2 min de descanso entre cada uno.',
      'Registrá el mejor tiempo.',
    ],
    reference: '4.0-4.5s bueno · <4.0s excelente',
    frequency: 'Cada 6-8 semanas',
    inputType: 'number',
    step: 0.01,
    timer: { label: 'Descanso entre sprints', seconds: 120 },
  },
  {
    type: 'AGILITY_505',
    label: 'Agilidad 505',
    icon: Zap,
    unit: 's',
    group: 'Físico',
    color: 'amber',
    equipment: ['Cronómetro', '2 conos (a 10m y 15m del punto de partida)'],
    instructions: [
      'Cono A (línea de partida), cono intermedio a 10m, cono B a 15m.',
      'Jugador parte del cono A corriendo hacia el cono B.',
      'Largás el cronómetro cuando cruza el cono intermedio.',
      'Toca el cono B con el pie, gira 180°, vuelve al cono intermedio.',
      'Parás cuando cruza de vuelta el cono intermedio.',
      '2 intentos con pie derecho de pivote, 1 con izquierdo.',
      'Registrá el mejor tiempo.',
    ],
    reference: '2.7-3.0s bueno para 8-9 años',
    frequency: 'Cada 6-8 semanas',
    inputType: 'number',
    step: 0.01,
    timer: { label: 'Descanso entre intentos', seconds: 60 },
  },
  {
    type: 'FLEXIBILITY',
    label: 'Flexibilidad (sit-and-reach)',
    icon: Zap,
    unit: 'cm',
    group: 'Físico',
    color: 'amber',
    equipment: ['Caja de sit-and-reach O banco + regla de 1m'],
    instructions: [
      'Jugador sentado, piernas estiradas, pies apoyados en la caja (nivel 0).',
      'Extiende brazos hacia adelante lentamente, sin rebotar.',
      'Empuja el marcador lo más lejos posible con las puntas de los dedos.',
      'Mantiene la posición 2 segundos.',
      'Registrá el mejor de 3 intentos.',
    ],
    tip: 'Negativo = no llega a los pies. Positivo = pasa los dedos.',
    reference: '+3 a +8 cm bueno · >+10 cm excelente',
    frequency: 'Cada 6-8 semanas',
    inputType: 'number',
    step: 0.5,
  },

  // ── Técnicas ─────────────────────────────────────────
  {
    type: 'BALL_CONTROL_30S',
    label: 'Dominio de balón 30s',
    icon: Activity,
    unit: 'reps',
    group: 'Técnico',
    color: 'emerald',
    equipment: ['Balón talla 4', 'Cronómetro'],
    instructions: [
      'Jugador da toques al aire (juggling) sin que el balón caiga.',
      'Cuenta los toques durante 30 segundos.',
      'Si se le cae, lo levanta y sigue — el cronómetro NO para.',
      '2 intentos. Registrá el mejor.',
    ],
    reference: '15-25 reps bueno · 40+ excelente',
    frequency: 'Cada 4 semanas',
    inputType: 'number',
    step: 1,
    timer: { label: 'Test 30 segundos', seconds: 30, countdown: true },
  },
  {
    type: 'PASS_ACCURACY',
    label: 'Precisión de pase /10',
    icon: Activity,
    unit: 'hits',
    group: 'Técnico',
    color: 'emerald',
    equipment: ['Balón talla 4', 'Arco chico O círculo de 4 conos (1m diámetro) a 10m'],
    instructions: [
      'Balón quieto, 10 pases consecutivos hacia el objetivo.',
      '5 con pie dominante + 5 con no dominante.',
      'Cuenta como acierto si el balón entra sin tocar bordes.',
      'Registrá el total de aciertos (0-10).',
    ],
    reference: 'Máximo 10 hits',
    frequency: 'Cada 4 semanas',
    inputType: 'number',
    step: 1,
    max: 10,
  },
  {
    type: 'SLALOM_DRIBBLE',
    label: 'Slalom conducción',
    icon: Activity,
    unit: 's',
    group: 'Técnico',
    color: 'emerald',
    equipment: ['Cronómetro', '5 conos separados 2m', 'Balón talla 4'],
    instructions: [
      'Jugador parte con balón en los pies desde la línea de salida.',
      'Conduce en slalom entre los 5 conos (10m).',
      'Da la vuelta al último cono.',
      'Vuelve recto con el balón a la línea de partida.',
      '2 intentos. Registrá el mejor.',
    ],
    reference: '8-10s bueno · <7s excelente',
    frequency: 'Cada 4 semanas',
    inputType: 'number',
    step: 0.01,
    timer: { label: 'Descanso entre intentos', seconds: 60 },
  },

  // ── Mental (al final, observacional) ─────────────────
  {
    type: 'CONCENTRATION',
    label: 'Concentración',
    icon: Brain,
    unit: '/5',
    group: 'Mental',
    color: 'purple',
    equipment: ['Ninguno — observación del profe'],
    instructions: [
      'Escala 1-5 basada en observación durante la sesión:',
      '1 = Se distrae constantemente, no sigue indicaciones.',
      '3 = Atiende pero se pierde en momentos clave.',
      '5 = Mantiene foco toda la sesión, anticipa instrucciones.',
    ],
    frequency: 'Al final de cada sesión',
    inputType: 'scale',
    max: 5,
  },
  {
    type: 'TEAMWORK',
    label: 'Trabajo en equipo',
    icon: Brain,
    unit: '/5',
    group: 'Mental',
    color: 'purple',
    equipment: ['Ninguno — observación del profe'],
    instructions: [
      'Escala 1-5 basada en observación:',
      '1 = Juega solo, no pasa, no ayuda.',
      '3 = Pasa cuando le conviene, responde al equipo.',
      '5 = Busca al compañero, celebra al equipo, cubre espacios.',
    ],
    frequency: 'Al final de cada sesión',
    inputType: 'scale',
    max: 5,
  },
  {
    type: 'LEADERSHIP',
    label: 'Liderazgo',
    icon: Brain,
    unit: '/5',
    group: 'Mental',
    color: 'purple',
    equipment: ['Ninguno — observación del profe'],
    instructions: [
      'Escala 1-5 basada en observación:',
      '1 = Pasivo, sigue sin opinar.',
      '3 = Da una instrucción o organiza una jugada.',
      '5 = Habla con compañeros, motiva, se ofrece de capitán.',
    ],
    frequency: 'Al final de cada sesión',
    inputType: 'scale',
    max: 5,
  },
];

// Kit recomendado para cubrir los 12 tests.
export const EQUIPMENT_KIT = {
  basic: {
    title: 'Kit básico (cubre 9-12 tests)',
    subtitle: 'Total aproximado: $72.000 CLP / USD 80-100',
    items: [
      { name: 'Cronómetro digital deportivo', use: 'Sprint, 505, dominio 30s, slalom', price: '$5.000-15.000 CLP', where: 'MercadoLibre, Decathlon, ferreterías deportivas' },
      { name: 'Cinta métrica 30m', use: 'Marcar distancias en cancha', price: '$8.000-15.000 CLP', where: 'Sodimac, Easy, MercadoLibre' },
      { name: 'Set de 10-12 conos de entrenamiento', use: 'Slalom, sprint, 505, pase', price: '$10.000-20.000 CLP', where: 'Decathlon (Kipsta), MercadoLibre "kit fútbol"' },
      { name: 'Balanza digital', use: 'Peso (antropometría)', price: '$15.000-25.000 CLP', where: 'Farmacias, Sodimac, MercadoLibre' },
      { name: 'Tiza deportiva o cinta americana', use: 'Marcar piso/pared para salto vertical', price: '$2.000 CLP', where: 'Ferretería' },
      { name: 'Regla de 1m o metro plegable', use: 'Salto vertical, flexibilidad', price: '$3.000-8.000 CLP', where: 'Ferretería' },
      { name: 'Balón #4 (talla niño 8-9)', use: 'Tests técnicos', price: '$10.000-20.000 CLP', where: 'Decathlon, Sporting, MercadoLibre' },
    ],
  },
  upgrade: {
    title: 'Upgrades (precisión profesional)',
    subtitle: 'Opcionales — sumar cuando haya presupuesto',
    items: [
      { name: 'Caja de sit-and-reach', use: 'Flexibilidad precisa', price: '$40.000-80.000 CLP', where: 'MercadoLibre "cajón flexibilidad"' },
      { name: 'Estadímetro portátil', use: 'Altura precisa', price: '$35.000-60.000 CLP', where: 'Tiendas médicas, MercadoLibre' },
      { name: 'Células fotoeléctricas (puertas de tiempo)', use: 'Sprints y 505 automáticos sin error humano', price: '$300.000-600.000 CLP', where: 'Witty Chile, Microgate (importado)' },
      { name: 'Tapiz de salto (jump mat)', use: 'Salto vertical preciso', price: '$150.000-250.000 CLP', where: 'MercadoLibre "jump mat"' },
      { name: 'Chronojump Boscosystem', use: 'Sistema open-source para saltos y sprints', price: '~USD 200 (importado)', where: 'chronojump.org' },
    ],
  },
};

export const STORES_BY_COUNTRY = [
  {
    country: '🇨🇱 Chile',
    stores: [
      { name: 'Decathlon (Costanera, Parque Arauco, Las Condes)', tag: 'Conos Kipsta, cronómetros, balones talla 4' },
      { name: 'MercadoLibre Chile', tag: 'Buscar "kit entrenamiento fútbol" — packs completos $25-40k' },
      { name: 'Sodimac / Easy', tag: 'Balanzas, cintas métricas, tiza' },
      { name: 'Sporting Chile, Ortiz Sport', tag: 'Material técnico fútbol' },
    ],
  },
  {
    country: '🇦🇷 Argentina',
    stores: [
      { name: 'MercadoLibre Argentina', tag: '"Kit fútbol entrenamiento"' },
      { name: 'Dexter, Stock Center', tag: 'Cronómetros, balones' },
      { name: 'Sportotal (Buenos Aires)', tag: 'Material técnico' },
    ],
  },
  {
    country: '🇺🇸 USA',
    stores: [
      { name: 'Amazon', tag: 'Sportline stopwatches, SKLZ/Bownet cones, JustJump mats' },
      { name: 'Dick\'s Sporting Goods', tag: 'General equipment' },
      { name: 'PerformBetter.com', tag: 'Equipamiento pro accesible' },
    ],
  },
];
